import { Button, cn } from '@hibro/ui-client';
import { type ReactNode, useEffect, useState } from 'react';

import { useTranslator } from '../../i18n/i18n';

/**
 * Who draws the window's title-bar chrome (§5.8):
 *
 * - `custom` — this window is frameless and we draw the buttons (Electron on
 *   Windows/Linux);
 * - `native` — the OS draws them and we must leave room for them (Electron on
 *   macOS, where the traffic lights sit over the top-left);
 * - `none` — there is no window to control (the browser runtime, 见 18).
 *
 * A single fact rather than an OS string, because "which OS is the user on" is a
 * *different* question with a different answer — a browser on macOS has no
 * traffic lights to avoid. Conflating the two is what made `'web'` a third value
 * in an OS enum; module UI reads the OS from `window.hibroHost` instead.
 */
export type WindowChrome = 'custom' | 'native' | 'none';

/**
 * Host-injected bridge to the frameless window's title-bar controls (§5.8).
 * Built in the app shell from the preload `hibroWindow` global so this package
 * stays free of the preload bridge (same pattern as `ExternalBridge`).
 */
export interface WindowControlsBridge {
  /** Who draws this window's chrome; see {@link WindowChrome}. */
  chrome: WindowChrome;
  /** Release channel (见 01 渠道身份): non-stable channels append a brand suffix. */
  channel?: string;
  minimize(): void;
  toggleMaximize(): void;
  close(): void;
  isMaximized(): Promise<boolean>;
  /** Subscribe to maximize/restore changes; returns a disposer. */
  onMaximizeChange(callback: (maximized: boolean) => void): () => void;
  /** 订阅窗口失活（失焦）/激活（聚焦）变化（true=失活 / false=激活）；返回取消订阅函数。 */
  onWindowBlur(callback: (blurred: boolean) => void): () => void;
  /** 切换真全屏（区别于最大化：铺满显示器、隐藏系统任务栏）。 */
  toggleFullScreen(): void;
  /** 当前是否处于真全屏。 */
  isFullScreen(): Promise<boolean>;
  /** 设置/取消窗口置顶（始终在其它窗口之上）。 */
  setAlwaysOnTop(flag: boolean): void;
  /** 当前是否置顶。 */
  isAlwaysOnTop(): Promise<boolean>;
  /** 设置窗口尺寸（宽/高，CSS 像素）。 */
  setSize(width: number, height: number): void;
  /** 设置窗口**最小尺寸**（拖拽缩放下限，CSS 像素）；传 0 = 解除该轴下限。 */
  setMinimumSize(width: number, height: number): void;
  /** 读取窗口当前尺寸（宽/高）。 */
  getSize(): Promise<{ width: number; height: number }>;
  /** 设置窗口相对当前显示器工作区的位置（CSS margin 语义：left/top 为距左/顶边、
   *  right/bottom 为距右/底边）；四边都传 0 = 完全居中。 */
  setPosition(margin: {
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
  }): void;
  /**
   * 把本窗口叫到前台。桌面是 show + restore + focus（含托盘里藏着的窗）；
   * 浏览器是 `window.focus()`。没能到前台时自己记一条 warn 并 resolve，不抛。
   *
   * 若这次把焦点从另一扇 HiBro 窗口借走，resolve 一个函数；弹层关掉时调用它，
   * 把焦点还回去。没有别的窗口可还时 resolve `undefined`。
   */
  present(): Promise<(() => Promise<void>) | undefined>;
}

export interface WindowControlsProps {
  bridge: WindowControlsBridge;
}

const ICON_STROKE = 'currentColor';

function MinimizeIcon(): ReactNode {
  return (
    <svg
      className="size-2.5"
      width={10}
      height={10}
      viewBox="0 0 10 10"
      aria-hidden="true"
    >
      <line x1={0} y1={5} x2={10} y2={5} stroke={ICON_STROKE} strokeWidth={1} />
    </svg>
  );
}

function MaximizeIcon(): ReactNode {
  return (
    <svg
      className="size-2.5"
      width={10}
      height={10}
      viewBox="0 0 10 10"
      aria-hidden="true"
    >
      <rect
        x={0.5}
        y={0.5}
        width={9}
        height={9}
        fill="none"
        stroke={ICON_STROKE}
        strokeWidth={1}
      />
    </svg>
  );
}

function RestoreIcon(): ReactNode {
  return (
    <svg
      className="size-2.5"
      width={10}
      height={10}
      viewBox="0 0 10 10"
      aria-hidden="true"
    >
      <rect
        x={0.5}
        y={2.5}
        width={7}
        height={7}
        fill="none"
        stroke={ICON_STROKE}
        strokeWidth={1}
      />
      <path
        d="M2.5 2.5 V0.5 H9.5 V7.5 H7.5"
        fill="none"
        stroke={ICON_STROKE}
        strokeWidth={1}
      />
    </svg>
  );
}

function CloseIcon(): ReactNode {
  return (
    <svg
      className="size-2.5"
      width={10}
      height={10}
      viewBox="0 0 10 10"
      aria-hidden="true"
    >
      <line
        x1={0}
        y1={0}
        x2={10}
        y2={10}
        stroke={ICON_STROKE}
        strokeWidth={1}
      />
      <line
        x1={10}
        y1={0}
        x2={0}
        y2={10}
        stroke={ICON_STROKE}
        strokeWidth={1}
      />
    </svg>
  );
}

interface ControlButtonProps {
  title: string;
  danger?: boolean;
  onClick: () => void;
  children: ReactNode;
}

function ControlButton({
  title,
  danger,
  onClick,
  children,
}: ControlButtonProps): ReactNode {
  return (
    <Button
      type="button"
      variant="ghost"
      title={title}
      aria-label={title}
      onClick={onClick}
      // `no-drag` so clicks aren't swallowed by the draggable header (§5.8).
      style={{ WebkitAppRegion: 'no-drag' }}
      className={cn(
        'h-7.5 w-10 cursor-pointer rounded-md text-(--app-color-text-secondary)',
        danger
          ? 'hover:bg-destructive hover:text-white'
          : 'hover:bg-muted hover:text-foreground',
      )}
    >
      {children}
    </Button>
  );
}

/**
 * Custom title-bar window controls (minimize / maximize-restore / close) for the
 * frameless workbench window (§5.8). On macOS the native traffic-light buttons
 * are used instead, so this renders nothing there; on Windows/Linux it draws the
 * three buttons, keeping the maximize/restore icon in sync with the OS window
 * state via `bridge.onMaximizeChange`.
 */
export function WindowControls({ bridge }: WindowControlsProps): ReactNode {
  const { t } = useTranslator();
  const [maximized, setMaximized] = useState(false);
  const showChrome = bridge.chrome === 'custom';

  useEffect(() => {
    if (!showChrome) {
      return;
    }
    let active = true;
    void bridge.isMaximized().then((value) => {
      if (active) {
        setMaximized(value);
      }
    });
    const dispose = bridge.onMaximizeChange(setMaximized);
    return () => {
      active = false;
      dispose();
    };
  }, [bridge, showChrome]);

  if (!showChrome) {
    return null;
  }

  return (
    <div
      className="flex items-center gap-x-1 ml-2"
      style={{
        WebkitAppRegion: 'no-drag',
      }}
    >
      <ControlButton
        title={t('workbench.window.minimize')}
        onClick={() => bridge.minimize()}
      >
        <MinimizeIcon />
      </ControlButton>
      <ControlButton
        title={
          maximized
            ? t('workbench.window.restore')
            : t('workbench.window.maximize')
        }
        onClick={() => bridge.toggleMaximize()}
      >
        {maximized ? <RestoreIcon /> : <MaximizeIcon />}
      </ControlButton>
      <ControlButton
        title={t('workbench.window.close')}
        danger
        onClick={() => bridge.close()}
      >
        <CloseIcon />
      </ControlButton>
    </div>
  );
}
